import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaStream } from '@react-icons/all-files/fa/FaStream';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaSync } from '@react-icons/all-files/fa/FaSync';
import Breadcrumb from '../../commons/Breadcrumb';
import PageHeader from '../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import ProfaseDataGrid, { ITEMS_PER_PAGE } from '../../commons/Datagrid';
import { DeleteButton, PlayButton, RenderActions } from '../../commons/DataGridActions';
import Confirm, { useConfirm } from '../../commons/Confirm';
import LinkCell from '../../commons/LinkCell';
import DateFormat from '../../commons/componentes/DateFormat';
import { useAppDispatch, useAppSelector } from '../../store';
import { getEntities, deleteEntity, reset } from '../../manager/workflow-instancia.reducer';
import type { ProcesosOutletContext } from './procesos-periodo-page';
import {
  parseProcesosWorkflowId,
  withWorkflowIdFilter,
  type ProcesosWorkflowLocationState,
} from './procesos-workflow-routes';
import { ESTADO_NO_CREADO, isProcesoNoCreado, procesosNoCreadoPeriodosPath, procesosNoCreadoPeriodosSearch, procesosRowId } from './procesos-no-creados';
import { empresaProcesosLabel } from './procesos-empresa-routes';

const PROCESOS_FILTERS = [
  { label: 'ID', name: 'id', single: true },
  { label: 'Workflow', name: 'workflowEmpresa.workflow.nombre' },
  { label: 'RFC', name: 'workflowEmpresa.empresa.rfc' },
  { label: 'Razón social', name: 'workflowEmpresa.empresa.razonSocial' },
  { label: 'Grupo', name: 'workflowEmpresa.empresa.grupoEmpresarial.alias' },
  { label: 'Periodo', name: 'periodoId', single: true, displayParam: 'periodoLabel', clearWith: ['incluirArchivado'] },
  {
    label: 'Incluir Archivados',
    name: 'incluirArchivado',
    single: true,
    type: 'select' as const,
    options: [{ value: 'S', label: 'Sí' }],
  },
  {
    label: 'Estado',
    name: 'estado',
    type: 'select' as const,
    options: [
      { value: 'NUEVO', label: 'Nuevo' },
      { value: 'GENERADO', label: 'Generado' },
      { value: 'EN_PROCESO', label: 'En Proceso' },
      { value: 'PENDIENTE_DE_CORREGIR', label: 'Pend. de Corregir' },
      { value: 'FINALIZADO', label: 'Finalizado' },
      { value: 'ANULADO', label: 'Anulado' },
      { value: ESTADO_NO_CREADO, label: 'No Creado' },
    ],
  },
];

interface ActividadesResumen {
  totalDeActividades: number;
  cantidadDeActividadesFinalizadas: number;
}

interface InstanciaEstadoResumen {
  estado: string;
  cantidad: number;
  porcentaje?: number;
}

const RESUMEN_ESTADOS_REQUERIDOS = [
  'NUEVO',
  'GENERADO',
  'EN_PROCESO',
  'PENDIENTE_DE_CORREGIR',
  'ANULADO',
  'FINALIZADO',
  ESTADO_NO_CREADO,
];

function filtersFromSearch(
  search: string,
  filterDefs: typeof PROCESOS_FILTERS,
): { filterName: string; filterValue: string }[] {
  const urlParams = new URLSearchParams(search);
  return filterDefs.flatMap(f => {
    const values = urlParams.getAll(f.name).filter(v => v !== '');
    if (f.single) {
      const v = values.length > 0 ? values[values.length - 1] : null;
      return v != null ? [{ filterName: f.name, filterValue: v }] : [];
    }
    return values.map(v => ({ filterName: f.name, filterValue: v }));
  });
}

function buildActividadesResumenParams(search: string): URLSearchParams {
  const params = new URLSearchParams();
  filtersFromSearch(search, PROCESOS_FILTERS).forEach(f => {
    params.append(f.filterName, f.filterValue);
  });
  return params;
}

function buildEstadosResumenParams(search: string): URLSearchParams {
  const params = new URLSearchParams();
  filtersFromSearch(search, PROCESOS_FILTERS)
    .filter(f => f.filterName !== 'estado')
    .forEach(f => {
      params.append(f.filterName, f.filterValue);
    });
  return params;
}

function normalizeEstadosResumenRows(rows: InstanciaEstadoResumen[]): InstanciaEstadoResumen[] {
  const byEstado = new Map(
    rows.map(row => [String(row.estado).toUpperCase(), row]),
  );
  const normalized = RESUMEN_ESTADOS_REQUERIDOS.map(estado => {
    const existing = byEstado.get(estado);
    return {
      estado,
      cantidad: Number(existing?.cantidad) || 0,
      porcentaje: existing?.porcentaje,
    };
  });
  rows.forEach(row => {
    const up = String(row.estado).toUpperCase();
    if (!RESUMEN_ESTADOS_REQUERIDOS.includes(up)) {
      normalized.push({
        estado: up,
        cantidad: Number(row.cantidad) || 0,
        porcentaje: row.porcentaje,
      });
    }
  });
  return normalized;
}

function setEstadoFilter(search: string, estado: string): string {
  const params = new URLSearchParams(search);
  params.delete('estado');
  params.set('estado', String(estado).toUpperCase());
  params.set('page', '1');
  return params.toString();
}

const ESTADO_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  NUEVO: { label: 'Nuevo', bg: '#f1f5f9', color: '#475569' },
  GENERADO: { label: 'Generado', bg: '#eff6ff', color: '#1d4ed8' },
  EN_PROCESO: { label: 'En Proceso', bg: '#eff6ff', color: '#1d4ed8' },
  PENDIENTE_DE_CORREGIR: { label: 'Pend. de Corregir', bg: '#fffbeb', color: '#d97706' },
  FINALIZADO: { label: 'Finalizado', bg: '#f0fdf4', color: '#15803d' },
  ANULADO: { label: 'Anulado', bg: '#fef2f2', color: '#b91c1c' },
  NO_CREADO: { label: 'No Creado', bg: '#f8fafc', color: '#475569' },
  EN_PROCESO_Y_ARCHIVADO: { label: 'Archivado', bg: '#f1f5f9', color: '#475569' },
  TERMINADO_Y_ARCHIVADO: { label: 'Archivado', bg: '#f1f5f9', color: '#475569' },
  FINALIZADO_Y_ARCHIVADO: { label: 'Archivado', bg: '#f1f5f9', color: '#475569' },
};

function instanciaEstadoLabel(estado: string): string {
  return ESTADO_CONFIG[estado.toUpperCase()]?.label ?? estado;
}

function InstanciaEstadoResumenLabel({
  estado,
  onClick,
}: {
  estado: string;
  onClick?: () => void;
}) {
  const label = instanciaEstadoLabel(estado);

  if (!onClick) {
    return <span className="procesos-estado-resumen__estado-label">{label}</span>;
  }

  return (
    <button
      type="button"
      className="procesos-estado-resumen__filter-btn"
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      title={`Filtrar por: ${label}`}
      aria-label={`Filtrar por ${label}`}
    >
      <span className="procesos-estado-resumen__estado-label">{label}</span>
    </button>
  );
}

function ProcesosEstadosResumenBar({
  rows,
  loading,
  onEstadoClick,
}: {
  rows: InstanciaEstadoResumen[];
  loading: boolean;
  onEstadoClick: (estado: string) => void;
}) {
  const total = rows.reduce((sum, row) => sum + (Number(row.cantidad) || 0), 0);
  const displayRows = normalizeEstadosResumenRows(rows);

  if (loading) {
    return (
      <div className="procesos-estado-resumen" role="status" aria-live="polite">
        <span className="procesos-estado-resumen__loading">Cargando instancias por estado…</span>
      </div>
    );
  }

  return (
    <div className="procesos-estado-resumen" role="status" aria-live="polite">
      {displayRows.map(row => {
        const cantidad = Number(row.cantidad) || 0;
        const pct = row.porcentaje != null
          ? Math.round(row.porcentaje)
          : total > 0
            ? Math.round((cantidad / total) * 100)
            : null;

        return (
          <div key={row.estado} className="procesos-estado-resumen__estado">
            <InstanciaEstadoResumenLabel
              estado={row.estado}
              onClick={() => onEstadoClick(row.estado)}
            />
            <div className="procesos-estado-resumen__metricas">
              <span className="procesos-estado-resumen__valor">{cantidad}</span>
              <span className="procesos-estado-resumen__valor">
                {pct == null ? 'N/D' : `${pct}%`}
              </span>
            </div>
          </div>
        );
      })}
      <div className="procesos-estado-resumen__total">
        <span className="procesos-estado-resumen__total-label">Total</span>
        <span className="procesos-estado-resumen__valor procesos-estado-resumen__valor--total">{total}</span>
      </div>
    </div>
  );
}

function ProcesosActividadesResumenBar({
  resumen,
  loading,
  onPeriodoClick,
  onWorkflowClick,
  onEmpresaClick,
}: {
  resumen: ActividadesResumen | null;
  loading: boolean;
  onPeriodoClick: () => void;
  onWorkflowClick: () => void;
  onEmpresaClick: () => void;
}) {
  const total = resumen?.totalDeActividades ?? 0;
  const finalizadas = resumen?.cantidadDeActividadesFinalizadas ?? 0;
  const pct = formatActividadesPorcentaje(total, finalizadas);

  return (
    <div className="procesos-resumen" role="status" aria-live="polite">
      <div className="procesos-resumen__item">
        <span className="procesos-resumen__label">Actividades finalizadas</span>
        <span className="procesos-resumen__value">
          {loading ? '…' : finalizadas}
        </span>
      </div>
      <div className="procesos-resumen__item">
        <span className="procesos-resumen__label">Total actividades</span>
        <span className="procesos-resumen__value">
          {loading ? '…' : total}
        </span>
      </div>
      <div className="procesos-resumen__trailing">
        <div className="procesos-resumen__item procesos-resumen__item--avance">
          <span className="procesos-resumen__label">Avance</span>
          {loading ? (
            <span className="procesos-resumen__loading">…</span>
          ) : pct == null ? (
            <span className="procesos-actividades-nd">N/D</span>
          ) : (
            <span className={avancePctClass(pct)}>{pct}%</span>
          )}
        </div>
        <div className="procesos-resumen__actions">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm procesos-resumen__periodo-btn"
            onClick={onPeriodoClick}
          >
            Periodo
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm procesos-resumen__periodo-btn"
            onClick={onWorkflowClick}
          >
            Workflow
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm procesos-resumen__periodo-btn"
            onClick={onEmpresaClick}
          >
            Empresa
          </button>
        </div>
      </div>
    </div>
  );
}

function EstadoBadge({ estado }: { estado?: string }) {
  if (!estado) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const cfg = ESTADO_CONFIG[estado.toUpperCase()] ?? { label: estado, bg: '#f1f5f9', color: '#475569' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: cfg.bg,
        color: cfg.color,
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </span>
  );
}

function BoolBadge({ value, trueLabel, falseLabel }: { value?: boolean; trueLabel: string; falseLabel: string }) {
  if (value == null) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const active = value === true;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        background: active ? '#eff6ff' : '#f8fafc',
        color: active ? '#1d4ed8' : '#64748b',
        whiteSpace: 'nowrap',
      }}
    >
      {active ? trueLabel : falseLabel}
    </span>
  );
}

function formatActividadesPorcentaje(total?: number | null, finalizadas?: number | null): number | null {
  const totalNum = total ?? 0;
  if (totalNum <= 0) return null;
  const done = finalizadas ?? 0;
  return Math.round((done / totalNum) * 100);
}

function avancePctClass(pct: number): string {
  if (pct >= 75) return 'procesos-avance-pct procesos-avance-pct--high';
  if (pct < 49) return 'procesos-avance-pct procesos-avance-pct--low';
  return 'procesos-avance-pct procesos-avance-pct--mid';
}

function ActividadesRatioCell({
  total,
  finalizadas,
}: {
  total?: number | null;
  finalizadas?: number | null;
}) {
  if (total == null && finalizadas == null) {
    return <span style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>—</span>;
  }

  const totalNum = total ?? 0;
  const done = finalizadas ?? 0;

  return <span className="procesos-actividades-ratio">{done}/{totalNum}</span>;
}

function ActividadesPorcentajeCell({
  total,
  finalizadas,
}: {
  total?: number | null;
  finalizadas?: number | null;
}) {
  const pct = formatActividadesPorcentaje(total, finalizadas);

  if (pct == null) {
    return <span className="procesos-actividades-nd">N/D</span>;
  }

  return <span className={avancePctClass(pct)}>{pct}%</span>;
}

export default function ProcesosPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => (s as any).workflowInstancia.entities);
  const totalItems = useAppSelector(s => (s as any).workflowInstancia.totalItems);
  const loading = useAppSelector(s => (s as any).workflowInstancia.loading);
  const updateSuccess = useAppSelector(s => (s as any).workflowInstancia.updateSuccess);
  const errorMessage = useAppSelector(s => (s as any).workflowInstancia.errorMessage);

  const [rows, setRows] = useState<any[]>([]);
  const [actividadesResumen, setActividadesResumen] = useState<ActividadesResumen | null>(null);
  const [estadosResumen, setEstadosResumen] = useState<InstanciaEstadoResumen[]>([]);
  const [resumenLoading, setResumenLoading] = useState(false);
  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();
  const lastParamsRef = useRef<any>(null);
  const workflowId = parseProcesosWorkflowId(location.pathname);
  const workflowNombre = (location.state as ProcesosWorkflowLocationState | null)?.workflowNombre;

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) {
      showToast('success', msg);
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRows(entities ?? []);
  }, [entities]);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      showToast('success', 'Proceso eliminado correctamente.');
      if (lastParamsRef.current) dispatch(getEntities(lastParamsRef.current));
      fetchResumenes();
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchResumenes = useCallback(async () => {
    setResumenLoading(true);
    try {
      const actividadesParams = buildActividadesResumenParams(location.search);
      const estadosParams = buildEstadosResumenParams(location.search);
      if (workflowId != null) {
        actividadesParams.set('workflowId', String(workflowId));
        estadosParams.set('workflowId', String(workflowId));
      }
      actividadesParams.append('cacheBuster', String(Date.now()));
      estadosParams.append('cacheBuster', String(Date.now()));
      const [actividadesRes, estadosRes] = await Promise.all([
        axios.get<ActividadesResumen>(
          `api/profase-workflow-instancias/actividades/resumen?${actividadesParams.toString()}`,
        ),
        axios.get<InstanciaEstadoResumen[]>(
          `api/profase-workflow-instancias/estados/resumen?${estadosParams.toString()}`,
        ),
      ]);
      setActividadesResumen(actividadesRes.data);
      setEstadosResumen(Array.isArray(estadosRes.data) ? estadosRes.data : []);
    } catch {
      setActividadesResumen(null);
      setEstadosResumen([]);
    } finally {
      setResumenLoading(false);
    }
  }, [location.search, workflowId]);

  useEffect(() => {
    fetchResumenes();
  }, [fetchResumenes]);

  const handleEstadoFilter = useCallback((estado: string) => {
    navigate({ pathname: location.pathname, search: setEstadoFilter(location.search, estado) });
  }, [navigate, location.pathname, location.search]);

  const refreshAfterPeriodoChange = useCallback(() => {
    if (lastParamsRef.current) dispatch(getEntities(lastParamsRef.current));
    fetchResumenes();
  }, [dispatch, fetchResumenes]);

  const handleRefresh = useCallback(() => {
    void fetchResumenes();

    const params = new URLSearchParams(location.search);
    const page = Number(params.get('page') ?? 1);
    const sortParam = params.get('sort') ?? 'id,asc';
    const filters = withWorkflowIdFilter(
      filtersFromSearch(location.search, PROCESOS_FILTERS),
      workflowId,
    );
    const fetchParams = {
      page: page - 1,
      sort: sortParam,
      filters,
      size: ITEMS_PER_PAGE,
    };

    lastParamsRef.current = fetchParams;
    dispatch(getEntities(fetchParams));
  }, [location.search, dispatch, fetchResumenes, workflowId]);

  const outletContext: ProcesosOutletContext = {
    refreshAfterPeriodoChange,
  };

  const fetchData = (params: any) => {
    const next = {
      ...params,
      filters: withWorkflowIdFilter(params.filters ?? [], workflowId),
    };
    lastParamsRef.current = next;
    dispatch(getEntities(next));
  };

  useEffect(() => {
    if (!lastParamsRef.current) return;
    const next = {
      ...lastParamsRef.current,
      filters: withWorkflowIdFilter(lastParamsRef.current.filters ?? [], workflowId),
    };
    lastParamsRef.current = next;
    dispatch(getEntities(next));
  }, [dispatch, workflowId]);

  const handleDelete = (row: any) => {
    ask(
      `¿Confirmas la eliminación del proceso #${row.id}?`,
      async () => {
        setConfirmLoading(true);
        await dispatch(deleteEntity(row.id));
        setConfirmLoading(false);
        dismiss();
      },
      { title: 'Eliminar proceso', variant: 'danger' },
    );
  };

  const handlePlayNoCreado = (row: any) => {
    const path = procesosNoCreadoPeriodosPath(row);
    if (!path) return;
    const overlaySearch = procesosNoCreadoPeriodosSearch(row);
    navigate(path, {
      state: {
        procesosSearch: location.search || `?${overlaySearch}`,
        returnPath: '/procesos',
        empresaLabel: empresaProcesosLabel(row.workflowEmpresa?.empresa),
        workflowNombre: row.workflowEmpresa?.workflow?.nombre,
      },
    });
  };

  const cols = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.5,
      renderCell: (params: any) => (
        isProcesoNoCreado(params.row) ? (
          <span style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>—</span>
        ) : (
          <LinkCell
            label={params.row.id != null ? String(params.row.id) : null}
            onClick={() =>
              navigate(
                `/actividades?workflowInstanciaId=${params.row.id}&misActividades=N&page=1`,
              )
            }
          />
        )
      ),
    },
    {
      field: 'workflowEmpresa.workflow.nombre',
      headerName: 'Workflow',
      flex: 2,
      renderCell: (params: any) => (
        <LinkCell
          label={params.row.workflowEmpresa?.workflow?.nombre ?? '—'}
          filterName="workflowEmpresa.workflow.nombre"
          filterValue={params.row.workflowEmpresa?.workflow?.nombre}
        />
      ),
    },
    {
      field: 'workflowEmpresa.empresa.rfc',
      headerName: 'RFC',
      flex: 1.2,
      renderCell: (params: any) => (
        <LinkCell
          label={params.row.workflowEmpresa?.empresa?.rfc ?? null}
          filterName="workflowEmpresa.empresa.rfc"
          filterValue={params.row.workflowEmpresa?.empresa?.rfc}
        />
      ),
    },
    {
      field: 'workflowEmpresa.empresa.razonSocial',
      headerName: 'Razón social',
      flex: 2,
      renderCell: (params: any) => (
        <LinkCell
          label={params.row.workflowEmpresa?.empresa?.razonSocial ?? null}
          filterName="workflowEmpresa.empresa.razonSocial"
          filterValue={params.row.workflowEmpresa?.empresa?.razonSocial}
        />
      ),
    },
    {
      field: 'workflowEmpresa.empresa.grupoEmpresarial.alias',
      headerName: 'Grupo',
      flex: 1.5,
      renderCell: (params: any) => (
        <LinkCell
          label={params.row.workflowEmpresa?.empresa?.grupoEmpresarial?.alias ?? null}
          filterName="workflowEmpresa.empresa.grupoEmpresarial.alias"
          filterValue={params.row.workflowEmpresa?.empresa?.grupoEmpresarial?.alias}
        />
      ),
    },
    {
      field: 'periodoActividadInstancia.periodo.descripcion',
      headerName: 'Periodo',
      flex: 1.2,
      renderCell: (params: any) => {
        const periodo = params.row.periodoActividadInstancia?.periodo;
        const descripcion = periodo?.descripcion?.trim();
        const periodoId = periodo?.id;
        if (!descripcion || periodoId == null) {
          return <span style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>—</span>;
        }
        return (
          <LinkCell
            label={descripcion}
            filterName="periodoId"
            filterValue={periodoId}
            filterDisplayParam="periodoLabel"
            adjustSearchParams={p => {
              if (String(periodo?.estado ?? '').toUpperCase() === 'ARCHIVADO') {
                p.set('incluirArchivado', 'S');
              } else {
                p.delete('incluirArchivado');
              }
            }}
          />
        );
      },
    },
    {
      field: 'actividadesAvance',
      headerName: 'Actividades',
      flex: 0.9,
      renderCell: (params: any) => (
        <ActividadesRatioCell
          total={params.row.totalDeActividades}
          finalizadas={params.row.cantidadDeActividadesFinalizadas}
        />
      ),
    },
    {
      field: 'creado',
      headerName: 'Creado',
      flex: 1,
      renderCell: (params: any) =>
        params.row.creado ? <DateFormat value={params.row.creado} /> : '—',
    },
    {
      field: 'manual',
      headerName: 'Manual',
      flex: 0.8,
      renderCell: (params: any) => (
        <BoolBadge value={params.row.manual} trueLabel="Sí" falseLabel="No" />
      ),
    },
    {
      field: 'calendarizado',
      headerName: 'Calendarizado',
      flex: 1,
      renderCell: (params: any) => (
        <BoolBadge value={params.row.calendarizado} trueLabel="Sí" falseLabel="No" />
      ),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      flex: 1.2,
      renderCell: (params: any) => <EstadoBadge estado={params.row.estado} />,
    },
    {
      field: 'avanceActividades',
      headerName: 'Avance',
      flex: 0.8,
      renderCell: (params: any) => (
        <ActividadesPorcentajeCell
          total={params.row.totalDeActividades}
          finalizadas={params.row.cantidadDeActividadesFinalizadas}
        />
      ),
    },
    {
      field: '__actions',
      headerName: '',
      flex: 0.5,
      renderCell: (params: any) => (
        <RenderActions>
          {isProcesoNoCreado(params.row) ? (
            <PlayButton
              title="Crear procesos"
              onClick={() => handlePlayNoCreado(params.row)}
              disabled={!procesosNoCreadoPeriodosPath(params.row)}
            />
          ) : (
            <DeleteButton onClick={() => handleDelete(params.row)} />
          )}
        </RenderActions>
      ),
    },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb
        items={[
          { label: 'Inicio', to: '/', icon: <FaHome /> },
          { label: 'Procesos', icon: <FaStream /> },
        ]}
      />

      <PageHeader
        icon={<FaStream />}
        iconColor="#f59e0b"
        title="Procesos"
        subtitle={
          workflowId != null
            ? `Procesos de ${workflowNombre?.trim() || `workflow #${workflowId}`}`
            : 'Instancias de workflow por empresa y periodo'
        }
        action={{
          label: (
            <FaSync
              style={{
                fontSize: 14,
                animation: loading || resumenLoading ? 'spin 0.7s linear infinite' : undefined,
              }}
            />
          ),
          onClick: handleRefresh,
          disabled: loading || resumenLoading,
        }}
      />

      <Confirm config={confirmConfig} />

      {alert && <PageAlert alert={alert} />}
      {errorMessage && (
        <div style={g.errorBanner} role="alert">
          {errorMessage}
        </div>
      )}

      <ProcesosEstadosResumenBar
        rows={estadosResumen}
        loading={resumenLoading}
        onEstadoClick={handleEstadoFilter}
      />

      <ProcesosActividadesResumenBar
        resumen={actividadesResumen}
        loading={resumenLoading}
        onPeriodoClick={() => navigate('/procesos/periodo-actividad-instancia?estado=GENERADO', {
          state: { procesosSearch: location.search },
        })}
        onWorkflowClick={() => navigate('/procesos/workflow', {
          state: {
            procesosSearch: location.search,
            returnPath: workflowId != null ? location.pathname : '/procesos',
            workflowNombre,
          },
        })}
        onEmpresaClick={() => navigate('/procesos/empresas', {
          state: {
            procesosSearch: location.search,
            returnPath: '/procesos',
          },
        })}
      />

      {workflowId != null ? (
        <div style={g.workflowFilter} role="status">
          <span>
            Mostrando procesos de{' '}
            <strong>{workflowNombre?.trim() || `workflow #${workflowId}`}</strong>
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => navigate(`/procesos${location.search}`)}
          >
            Quitar filtro
          </button>
        </div>
      ) : null}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={PROCESOS_FILTERS}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
        getRowId={procesosRowId}
      />

      <Outlet context={outletContext} />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24, padding: '0 4px' },
  errorBanner: {
    padding: '10px 14px',
    background: '#fef2f2',
    color: '#b91c1c',
    border: '1px solid #fecaca',
    borderRadius: 8,
    fontSize: 13,
  },
  workflowFilter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    padding: '8px 12px',
    borderRadius: 8,
    background: 'var(--crm-surface-muted, #f8fafc)',
    fontSize: 13,
  },
};
